import React, { useMemo, useState } from 'react';
import { coffeeDispatches } from '../lib/api';
import { inr } from '../lib/format';
import { Plus, Trash2, Layers } from 'lucide-react';

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const emptyComp = () => ({ _uid: uid(), category_id: '', roast_level: '', quantity_kg: '' });
const emptyBlend = () => ({ _uid: uid(), blend_name: '', sale_price_per_kg: '', components: [emptyComp()] });

export const DispatchFormModal = ({ cats, stock, costs, clientNames, blendNames = [], onClose, onSaved }) => {
  const [form, setForm] = useState({
    client_name: '', invoice_no: '',
    dispatch_date: new Date().toISOString().split('T')[0], notes: '',
    payment_status: 'Unpaid', amount_paid: '',
  });
  const [blends, setBlends] = useState([emptyBlend()]);
  const [saving, setSaving] = useState(false);

  const availFor = (catId, lvl) =>
    stock.find((s) => s.category_id === catId && s.roast_level === lvl)?.quantity_kg || 0;
  const costFor = (catId, lvl) =>
    costs.find((c) => c.category_id === catId && c.roast_level === lvl)?.cost_per_kg || 0;

  const setBlend = (bIdx, patch) =>
    setBlends((prev) => prev.map((b, i) => (i === bIdx ? { ...b, ...patch } : b)));
  const setComp = (bIdx, cIdx, patch) =>
    setBlends((prev) => prev.map((b, i) => (i === bIdx
      ? { ...b, components: b.components.map((c, j) => (j === cIdx ? { ...c, ...patch } : c)) }
      : b)));

  const blendStats = (b) => {
    let kg = 0, cost = 0;
    b.components.forEach((c) => {
      const q = parseFloat(c.quantity_kg) || 0;
      kg += q;
      cost += q * costFor(c.category_id, c.roast_level);
    });
    const price = parseFloat(b.sale_price_per_kg) || 0;
    return { kg, cost, costPerKg: kg > 0 ? cost / kg : 0, lineTotal: kg * price };
  };

  const totals = useMemo(() => {
    let kg = 0, sale = 0, cost = 0;
    blends.forEach((b) => {
      const s = blendStats(b);
      kg += s.kg;
      cost += s.cost;
      sale += s.lineTotal;
    });
    return { kg, sale, cost, profit: sale - cost };
  }, [blends, costs]);

  const usedByKey = useMemo(() => {
    const used = {};
    blends.forEach((b) => b.components.forEach((c) => {
      if (!c.category_id || !c.roast_level) return;
      const key = `${c.category_id}|${c.roast_level}`;
      used[key] = (used[key] || 0) + (parseFloat(c.quantity_kg) || 0);
    }));
    return used;
  }, [blends]);

  const hasOverStock = useMemo(() =>
    Object.entries(usedByKey).some(([key, qty]) => {
      const [catId, lvl] = key.split('|');
      return qty > availFor(catId, lvl) + 1e-6;
    }), [usedByKey, stock]);

  const canSubmit = !hasOverStock && blends.length > 0 &&
    blends.every((b) =>
      b.blend_name.trim() &&
      b.components.length > 0 &&
      b.components.every((c) => c.category_id && c.roast_level && parseFloat(c.quantity_kg) > 0)
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await coffeeDispatches.create({
        client_name: form.client_name.trim(),
        invoice_no: form.invoice_no.trim(),
        dispatch_date: new Date(form.dispatch_date).toISOString(),
        notes: form.notes,
        payment_status: form.payment_status,
        amount_paid: parseFloat(form.amount_paid) || 0,
        items: blends.map((b) => ({
          blend_name: b.blend_name.trim(),
          sale_price_per_kg: parseFloat(b.sale_price_per_kg) || 0,
          components: b.components.map((c) => ({
            category_id: c.category_id,
            roast_level: c.roast_level,
            quantity_kg: parseFloat(c.quantity_kg),
          })),
        })),
      });
      onSaved();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save dispatch');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b flex justify-between items-center shrink-0">
          <h2 className="text-xl font-bold text-gray-800">Log Dispatch</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl" data-testid="dispatch-form-close">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
              <input type="text" list="client-names-list" value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                placeholder="e.g. Blue Tokai Cafe"
                data-testid="dispatch-client-input"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
              <datalist id="client-names-list">
                {clientNames.map((n) => <option key={n} value={n} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice No.</label>
              <input type="text" value={form.invoice_no}
                onChange={(e) => setForm({ ...form, invoice_no: e.target.value })}
                placeholder="Auto-generated if blank"
                data-testid="dispatch-invoice-input"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input type="date" required value={form.dispatch_date}
                onChange={(e) => setForm({ ...form, dispatch_date: e.target.value })}
                data-testid="dispatch-date-input"
                className="w-full px-3 py-2 border rounded-lg" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-800">Blends in this dispatch</label>
              <button type="button" onClick={() => setBlends((prev) => [...prev, emptyBlend()])}
                data-testid="dispatch-add-blend-button"
                className="flex items-center gap-1 text-sm font-semibold text-violet-600 hover:text-violet-800">
                <Plus className="w-4 h-4" /> Add Blend
              </button>
            </div>
            <datalist id="blend-names-list">
              {blendNames.map((n) => <option key={n} value={n} />)}
            </datalist>
            <div className="space-y-4">
              {blends.map((b, bIdx) => {
                const s = blendStats(b);
                return (
                  <div key={b._uid} className="border-2 border-violet-200 rounded-xl p-3 bg-violet-50/40" data-testid={`dispatch-blend-${bIdx}`}>
                    <div className="flex items-start gap-2">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 flex-1">
                        <div className="sm:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            <Layers className="w-3 h-3 inline mr-1 text-violet-500" />Blend Name *
                          </label>
                          <input type="text" required list="blend-names-list" value={b.blend_name}
                            onChange={(e) => setBlend(bIdx, { blend_name: e.target.value })}
                            placeholder="e.g. House Blend / Espresso Blend"
                            data-testid={`blend-name-${bIdx}`}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-violet-400" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Sale Price ₹/kg</label>
                          <input type="number" step="0.01" min="0" value={b.sale_price_per_kg}
                            onChange={(e) => setBlend(bIdx, { sale_price_per_kg: e.target.value })}
                            placeholder="0"
                            data-testid={`blend-price-${bIdx}`}
                            className="w-full px-3 py-2 border rounded-lg text-sm" />
                        </div>
                      </div>
                      {blends.length > 1 && (
                        <button type="button" onClick={() => setBlends((prev) => prev.filter((_, i) => i !== bIdx))}
                          data-testid={`blend-remove-${bIdx}`}
                          className="p-1.5 mt-5 text-red-500 hover:bg-red-50 rounded" title="Remove blend">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div className="mt-3 space-y-2">
                      {b.components.map((c, cIdx) => {
                        const avail = c.category_id && c.roast_level ? availFor(c.category_id, c.roast_level) : null;
                        const cost = c.category_id && c.roast_level ? costFor(c.category_id, c.roast_level) : 0;
                        const usedKey = `${c.category_id}|${c.roast_level}`;
                        const over = avail !== null && (usedByKey[usedKey] || 0) > avail + 1e-6;
                        return (
                          <div key={c._uid} className="bg-white border rounded-lg p-2" data-testid={`blend-comp-${bIdx}-${cIdx}`}>
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Coffee *</label>
                                <select required value={c.category_id}
                                  onChange={(e) => setComp(bIdx, cIdx, { category_id: e.target.value })}
                                  data-testid={`blend-comp-category-${bIdx}-${cIdx}`}
                                  className="w-full px-2 py-1.5 border rounded-lg text-sm">
                                  <option value="">— select —</option>
                                  {cats.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Roast *</label>
                                <select required value={c.roast_level}
                                  onChange={(e) => setComp(bIdx, cIdx, { roast_level: e.target.value })}
                                  data-testid={`blend-comp-level-${bIdx}-${cIdx}`}
                                  className="w-full px-2 py-1.5 border rounded-lg text-sm">
                                  <option value="">— level —</option>
                                  {['Light', 'Medium', 'Dark'].map((lvl) => (
                                    <option key={lvl} value={lvl}>
                                      {lvl}{c.category_id ? ` (${availFor(c.category_id, lvl).toFixed(1)} kg)` : ''}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Qty (kg) *</label>
                                <input type="number" step="0.01" min="0.01" required value={c.quantity_kg}
                                  onChange={(e) => setComp(bIdx, cIdx, { quantity_kg: e.target.value })}
                                  data-testid={`blend-comp-qty-${bIdx}-${cIdx}`}
                                  className={`w-full px-2 py-1.5 border rounded-lg text-sm ${over ? 'border-red-400 bg-red-50' : ''}`} />
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-1.5 text-xs">
                              <div className="text-gray-500 space-x-3">
                                {avail !== null && (
                                  <span className={over ? 'text-red-600 font-semibold' : ''}>
                                    Available: {avail.toFixed(2)} kg{over ? ' — not enough stock!' : ''}
                                  </span>
                                )}
                                {cost > 0 && <span>Est. cost: {inr(cost)}/kg</span>}
                              </div>
                              {b.components.length > 1 && (
                                <button type="button"
                                  onClick={() => setBlend(bIdx, { components: b.components.filter((_, j) => j !== cIdx) })}
                                  data-testid={`blend-comp-remove-${bIdx}-${cIdx}`}
                                  className="p-1 text-red-400 hover:bg-red-50 rounded">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="flex items-center justify-between mt-2">
                      <button type="button"
                        onClick={() => setBlend(bIdx, { components: [...b.components, emptyComp()] })}
                        data-testid={`blend-add-comp-${bIdx}`}
                        className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800">
                        <Plus className="w-3.5 h-3.5" /> Add Coffee to Blend
                      </button>
                      <div className="text-xs text-gray-600 space-x-3" data-testid={`blend-summary-${bIdx}`}>
                        <span className="font-semibold">{s.kg.toFixed(2)} kg</span>
                        {s.costPerKg > 0 && <span>Est. cost {inr(s.costPerKg)}/kg</span>}
                        {s.lineTotal > 0 && <span className="font-semibold text-gray-800">Total: {inr(s.lineTotal)}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm" data-testid="dispatch-form-totals">
            <div><p className="text-xs text-blue-700 uppercase">Total Qty</p><p className="font-bold text-blue-900">{totals.kg.toFixed(2)} kg</p></div>
            <div><p className="text-xs text-blue-700 uppercase">Sale Value</p><p className="font-bold text-blue-900">{inr(totals.sale)}</p></div>
            <div><p className="text-xs text-blue-700 uppercase">Est. Cost</p><p className="font-bold text-blue-900">{inr(totals.cost)}</p></div>
            <div><p className="text-xs text-blue-700 uppercase">Gross Profit</p>
              <p className={`font-bold ${totals.profit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{inr(totals.profit)}</p></div>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment</label>
              <div className="flex gap-2">
                {['Unpaid', 'Partial', 'Paid'].map((st) => (
                  <button key={st} type="button"
                    onClick={() => setForm({ ...form, payment_status: st })}
                    data-testid={`dispatch-payment-${st.toLowerCase()}`}
                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold border-2 transition ${
                      form.payment_status === st
                        ? (st === 'Paid' ? 'bg-emerald-500 text-white border-emerald-600' :
                           st === 'Partial' ? 'bg-amber-500 text-white border-amber-600' :
                           'bg-rose-500 text-white border-rose-600')
                        : 'bg-white text-gray-700 border-gray-200'
                    }`}>{st}</button>
                ))}
              </div>
            </div>
            {form.payment_status === 'Partial' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount Received (₹)</label>
                <input type="number" step="0.01" min="0" value={form.amount_paid}
                  onChange={(e) => setForm({ ...form, amount_paid: e.target.value })}
                  data-testid="dispatch-amount-paid-input"
                  className="w-36 px-3 py-2 border rounded-lg" />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea rows="2" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. Courier via BlueDart"
              className="w-full px-3 py-2 border rounded-lg" />
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={!canSubmit || saving}
              data-testid="dispatch-save-button"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? 'Saving…' : 'Save Dispatch'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
